import {
  startTransition,
  use,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";

import { CHUNK_SIZE } from "lib/content/progressive-content";
import { ContentContext } from "lib/content/use-content";
import { buildVirtualLayout, measureLayout } from "lib/layout/capture-layout";
import { ContentLayoutContext } from "lib/layout/use-content-layout";
import { useContentStyles } from "lib/layout/use-content-styles";
import { SettingsContext } from "lib/settings/use-settings";
import { useToc } from "lib/toc/use-toc";
import type {
  ContentNode as ContentNodeType,
  TableOfContents,
  VirtualLayout,
} from "lib/types";

import { ContentPart } from "app/content-part";
import css from "app/content.module.scss";

function useMeasurementLoop(
  contentRef: RefObject<HTMLDivElement>,
  content: ContentNodeType[][],
  totalParts: number,
  toc: TableOfContents,
  cols: number,
  layout: VirtualLayout | null,
  setLayout: (layout: VirtualLayout) => void,
  isResizingRef: RefObject<boolean>,
  setMeasurementProgress: (
    progress: { measured: number; total: number } | null
  ) => void
) {
  const [measuredUpTo, setMeasuredUpTo] = useState(0);

  useEffect(() => {
    if (isResizingRef.current) return;

    const node = contentRef.current;
    if (node && content.length > 0 && toc && layout?.mode !== "virtual") {
      const measured = measureLayout(node, cols);
      if (!measured) return;

      const result = buildVirtualLayout(measured, content, toc, 0);
      const measuredParts = Object.keys(result.parts).length;
      startTransition(() => {
        setMeasurementProgress({
          measured: measuredParts,
          total: totalParts,
        });
        if (measuredParts >= totalParts) {
          setMeasurementProgress(null);
          setLayout(result);
        } else {
          setMeasuredUpTo(measuredParts);
        }
      });
    }
  });

  return measuredUpTo;
}

export function FullContent() {
  const { content } = use(ContentContext)!;
  const { toc } = useToc();
  const { setLayout, layout, isResizingRef, setMeasurementProgress } =
    use(ContentLayoutContext)!;
  const { cols } = use(SettingsContext)!;
  const { content: contentStyles } = useContentStyles(layout);

  const totalParts = toc?.Parts?.length ?? 0;
  const contentRef = useRef<HTMLDivElement>(null);

  let contiguousCount = 0;
  while (contiguousCount < content.length && content[contiguousCount])
    contiguousCount++;
  const loadedContent = content.slice(0, contiguousCount);

  const measuredUpTo = useMeasurementLoop(
    contentRef,
    loadedContent,
    totalParts,
    toc,
    cols,
    layout,
    setLayout,
    isResizingRef,
    setMeasurementProgress
  );

  if (contiguousCount === 0) return null;
  if (layout?.mode === "virtual") return null;

  const partsToRender = Math.min(measuredUpTo + CHUNK_SIZE, contiguousCount);

  return (
    <div ref={contentRef} style={contentStyles} className={css.fullContent}>
      {loadedContent.slice(0, partsToRender).map((part, index) => (
        <ContentPart key={`full-part-${index}`} part={part} partIndex={index} />
      ))}
    </div>
  );
}
